import { api } from "./apiClient";

export interface PipelineEstado {
    numero: number;
    monto: number;
    ids?: string[];
}

/** Solicitud resumida para el visor "Ver Solicitudes" del Pipeline */
export interface PipelineSolicitudItem {
    id: string;
    solicitudNumeroControl?: string;
    clienteNombre?: string;
    clienteApellidoPaterno?: string;
    clienteApellidoMaterno?: string;
    clienteEmpresa?: string;
    producto?: string;
    montoSolicitado?: number;
    montoAutorizado?: number;
    solicitudEstatus?: string;
    fechaContacto?: string | null;
    fechaEntregaARiesgos?: string | null;
    fechaContratoImpreso?: string | null;
    asesorNombre?: string;
    ownerID?: string;
    inheritedID?: string;
}

export interface PipelineData {
    producto: string;
    fecha: string;
    asesor: string;
    CONTACTO: PipelineEstado;
    "ENTREGA A RIESGOS": PipelineEstado;
    AUTORIZADO: PipelineEstado;
    "CONTRATO IMPRESO": PipelineEstado;
    "FIRMAR CONTRATO": PipelineEstado;
    ENPROCESO: PipelineEstado;
    FONDEADO: PipelineEstado;
    RECHAZADO: PipelineEstado;
    TOTAL: PipelineEstado;
    /** Solicitudes de esta fila para el visor "Ver Solicitudes" */
    solicitudes?: PipelineSolicitudItem[];
}

export interface ScoreCardMetrica {
    total: number;
    ids: string[];
    monto: number;
    montoComision?: number;
    factorComision?: number;
    nivel?: string;
    porcientoCumplimiento?: number;
}

export interface ScoreCardAsesor {
    contactados: ScoreCardMetrica;
    entregadosARiesgos: ScoreCardMetrica;
    autorizados: ScoreCardMetrica;
    total: number;
    montoTotal: number;
    montoComision: number;
    factorComision: number;
    nivel: string;
    color: string;
    bgcolor: string;
}

export interface ScoreCardTotales {
    CN?: {
        contactados: number;
        entregadosARiesgos: number;
        autorizados: number;
        monto: number;
        montoComision: number;
    };
    AN?: {
        contactados: number;
        entregadosARiesgos: number;
        autorizados: number;
        monto: number;
        montoComision: number;
    };
    MIX?: {
        contactados: number;
        entregadosARiesgos: number;
        autorizados: number;
        monto: number;
        montoComision: number;
    };
}

/** Solicitud resumida por intervalo para el visor "Ver Solicitudes" del ScoreCard */
export interface ScoreCardSolicitudItem {
    id: string;
    solicitudNumeroControl?: string;
    clienteNombre?: string;
    clienteApellidoPaterno?: string;
    clienteApellidoMaterno?: string;
    clienteEmpresa?: string;
    producto?: string;
    montoAutorizado?: number;
    solicitudEstatus?: string;
    fechaContacto?: string | null;
    fechaEntregaARiesgos?: string | null;
    fechaContratoImpreso?: string | null;
    asesorNombre?: string;
    ownerID?: string;
    inheritedID?: string;
}

export interface ScoreCardIntervalo {
    fecha: string;
    intervaloInicio: string;
    intervaloFin: string;
    metasDia: {
        contactados: number;
        entregadosARiesgos: number;
        autorizados: number;
    };
    scorecard: {
        CN?: Record<string, ScoreCardAsesor>;
        AN?: Record<string, ScoreCardAsesor>;
        MIX?: Record<string, ScoreCardAsesor>;
        Seguros?: Record<string, ScoreCardAsesor>;
    };
    /** Solicitudes del intervalo para el visor "Ver Solicitudes" */
    solicitudes?: ScoreCardSolicitudItem[];
    /** Totales del día (suma de todos los asesores) */
    totalesDia?: ScoreCardTotales;
    /** Totales del día anterior para comparación */
    totalesDiaAnterior?: ScoreCardTotales;
    /** Fecha del día anterior */
    fechaDiaAnterior?: string;
}

export interface ScoreCardData extends ScoreCardIntervalo {}

export interface ColocacionSerie {
    dia: number;
    mesLabel?: string;  // For Mensual frequency
    cuenta: number;
    montoSolicitado: number;
    montoAutorizado: number;
    montoTransferencia: number;
    cuentaAcumulado: number;
    montoSolicitadoAcumulado: number;
    montoAutorizadoAcumulado: number;
    montoTransferenciaAcumulado: number;
}

export interface ColocacionProducto {
    producto: string;
    serie: ColocacionSerie[];
}

export interface ColocacionIntervalo {
    inicio: string;
    fin: string;
    mes: string;
    anio: number;
    productos: ColocacionProducto[];
}

export interface ColocacionData {
    frecuencia: string;
    fechaInicio: string;
    fechaFin: string;
    series: ColocacionIntervalo[];
}

export interface ColocacionSeguro {
    total: number;
    financiado: number;
}

export interface ColocacionSolicitudDetalle {
    id: string;
    numeroControl: string;
    cliente: string;
    fechaContratoImpreso: string;
    montoAutorizado: number;
    montoTransferencia: number;
    comision: number;
    seguroFinanciado: number;
    seguro: string;
    // Campos adicionales (legacy parity)
    empresa?: string;
    estatus?: string;
    expedienteResguardoStatus?: string;
    excepcionDocumentada1?: string;
    excepcionDocumentada2?: string;
    excepcionDocumentada3?: string;
    expedienteExigible?: string;
    asesor?: string;
}

export interface ColocacionAsesor {
    totalCreditos: number;
    totalCreditosNuevos: number;
    totalCreditosCleanUps: number;
    montoTotal: number;
    montoTotalTransferencia: number;
    montoTotalTransferenciaNuevos: number;
    montoTotalTransferenciaCleanUps: number;
    montoTotalComisiones: number;
    montoTotalSegurosFinanciados: number;
    seguros: {
        [monto: string]: ColocacionSeguro;
    };
    solicitudes: ColocacionSolicitudDetalle[];
    // Campos de excepciones y expedientes (legacy parity)
    totalExcepciones?: number;
    totalExpedientesConExcepcion?: number;
    expedientesComercial?: number;
    expedientesRiesgos?: number;
    expedientesBoveda?: number;
    // Stub fields
    eRatificacionDomiciliaria?: number;
    eAvanceCleanUp?: number;
    e1_7CleanUp?: number;
    eBuro?: number;
}

export interface ColocacionTotales {
    totalCreditos: number;
    totalCreditosNuevos: number;
    totalCreditosCleanUps: number;
    montoTotal: number;
    montoTotalTransferencia: number;
    montoTotalTransferenciaNuevos: number;
    montoTotalTransferenciaCleanUps: number;
    montoTotalComisiones: number;
    montoTotalSegurosFinanciados: number;
    // Campos de excepciones y expedientes
    totalExcepciones?: number;
    totalExpedientesConExcepcion?: number;
    expedientesComercial?: number;
    expedientesRiesgos?: number;
    expedientesBoveda?: number;
}

export interface ColocacionTabularData {
    fechaInicio: string;
    fechaFin: string;
    datosPorProducto: {
        [producto: string]: {
            [asesor: string]: ColocacionAsesor;
        };
    };
    totalesPorProducto: {
        [producto: string]: ColocacionTotales;
    };
    granTotal: ColocacionTotales;
}

interface ProductoStats {
    creditos: number;
    autorizado: number;
    transferencia: number;
    nuevos: number;
    montoNuevos: number;
    porcNumNuevos: number;
    porcMontoNuevos: number;
    comisiones: number;
}

interface SeguroStats {
    count25k: number;
    count50k: number;
    count75k: number;
    count100k: number;
    total: number;
    financiado: number;
}

interface DetalleTotales {
    creditos: number;
    montoAutorizado: number;
    montoTransferencia: number;
    comision: number;
    seguroFinanciado: number;
    seguro25k: number;
    seguro50k: number;
    seguro75k: number;
    seguro100k: number;
}

interface DetalleAsesor {
    asesor: string;
    registros: any[];
    totalNomina: DetalleTotales;
    totalAdelanto: DetalleTotales;
}

export interface ExcepcionesPoliticasData {
    resumenPorProducto: Array<{
        producto: string;
        totalCreditos: number;
        montoTotalAutorizado: number;
        montoTotalTransferencia: number;
        montoTotalComisiones: number;
        montoTotalSegurosFinanciados: number;
    }>;
    resumenPorAsesor: Array<{
        asesor: string;
        nomina: ProductoStats;
        adelantoNomina: ProductoStats;
        seguro: SeguroStats;
    }>;
    detallePorAsesor: DetalleAsesor[];
    fechaReporte: string;
}

export interface ExpedientesFaltantesData {
  resumenEntregasSemanales: Array<{
    año: number | string;
    producto: string;
    semanaAnterior: number;
    semanaActual: number;
  }>;
  resumenPorProducto: Array<{
    año: number | string;
    producto: string;
    fondeados: number;
    rechazados: number;
    total: number;
  }>;
  resumenPorAsesor: Array<{
    asesor: string;
    producto: string;
    año: number | string;
    fondeados: number;
    rechazados: number;
    total: number;
  }>;
  detalle: any[];
  fechaExigibilidad: string;
  anios?: number[];
}

export const reportesService = {
    getPipeline: (params: { 
        fechaInicio?: string; 
        fechaFin?: string; 
        fecha?: string;
        producto?: string;
        frecuencia?: string;
    } = {}) => {
        const query = new URLSearchParams();
        if (params.fechaInicio) query.append('fecha_inicio', params.fechaInicio);
        if (params.fechaFin) query.append('fecha_fin', params.fechaFin);
        if (params.fecha) query.append('fecha', params.fecha);
        if (params.producto) query.append('producto', params.producto);
        if (params.frecuencia) query.append('frecuencia', params.frecuencia);
        return api.get<PipelineData[]>(`/reportes/pipeline?${query.toString()}`);
    },

    getScoreCard: (params: { 
        fechaInicio?: string; 
        fechaFin?: string; 
        fecha?: string;
        frecuencia?: string;
        /** Si se envía, el backend devuelve comparativa: mes del día y 2 meses anteriores (1 al mismo día). */
        fechaReferencia?: string;
    } = {}) => {
        const query = new URLSearchParams();
        if (params.fechaInicio) query.append('fecha_inicio', params.fechaInicio);
        if (params.fechaFin) query.append('fecha_fin', params.fechaFin);
        if (params.fecha) query.append('fecha', params.fecha);
        if (params.frecuencia) query.append('frecuencia', params.frecuencia);
        if (params.fechaReferencia) query.append('fecha_referencia', params.fechaReferencia);
        return api.get<ScoreCardData[]>(`/reportes/scorecard?${query.toString()}`);
    },

    getColocacion: (params: {
        fechaInicio: string;
        fechaFin: string;
        frecuencia?: string;
        accion?: 'Graficar' | 'Tabular';
    }) => {
        const query = new URLSearchParams();
        query.append('fecha_inicio', params.fechaInicio);
        query.append('fecha_fin', params.fechaFin);
        if (params.frecuencia) query.append('frecuencia', params.frecuencia);
        if (params.accion) query.append('accion', params.accion);

        if (params.accion === 'Tabular') {
            return api.get<ColocacionTabularData>(`/reportes/colocacion?${query.toString()}`);
        } else {
            return api.get<ColocacionData>(`/reportes/colocacion?${query.toString()}`);
        }
    },

    getCumplimiento: (params: {
        accion: 'Excepciones a Políticas' | 'Expedientes Faltantes';
        fechaInicio?: string;
        fechaFin?: string;
        frecuencia?: string;
    }) => {
        const query = new URLSearchParams();
        query.append('accion', params.accion);
        if (params.fechaInicio) query.append('fecha_inicio', params.fechaInicio);
        if (params.fechaFin) query.append('fecha_fin', params.fechaFin);
        if (params.frecuencia) query.append('frecuencia', params.frecuencia);
        return api.get<ExcepcionesPoliticasData | ExpedientesFaltantesData>(`/reportes/cumplimiento?${query.toString()}`);
    },
};
